<template>
  <div>
    <v-container>
      <div v-show="isVisibleProp"></div>
      <v-row>
        <v-col cols="3">
          <v-text-field id="glDailyTransItemFyText"
            class="gl-daily-trans-item-fy-text"
            label="Fiscal Year"
            v-model.number="editedItem.fiscalYear"
            type="number"
            readonly
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <ClarionDateControl
            :isIsoDate="true"
            label="Business Date"
            v-model="editedItem.businessDate"
            :isDisabled="isReadOnly"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="editedItem.dailySection"
            :items="glDailySections"
            id="glDailyTransItemDailySectionSelect"
            class="gl-daily-trans-item-daily-section-select"
            label="Display Section"
            item-text="description"
            item-value="name"
            autofocus
            :rules="reqdSelectValidation"
            :readonly="isReadOnly"
            @focus="roundAmount"
          ></v-select>
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="editedItem.order"
            id="glDailyTransItemOrderNoText"
            class="gl-daily-trans-item-orderNo-text"
            label="Order No"
            :readonly="isReadOnly"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3"
          v-if="showFunds"
        >
          <v-autocomplete
            v-model="editedItem.fund"
             id="glDailyTransItemFundSelect"
           class="gl-daily-trans-item-fund-select"
            label="Fund"
            :items="funds"
            item-text="fundDesc"
            item-value="_id"
            :rules="selectingFundValidation"
            :hint="staleFundWarning"
            :persistent-hint="!!staleFundWarning"
            :readonly="isReadOnly"
            @change=checkBank
            ref="isFundRef"
            @keydown.tab.prevent="handleTab(['isBankRef', 'isFromNoRef'])"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3"
          v-if="showBanks"
        >
          <v-autocomplete
            v-model="editedItem.bank"
            :items="banks"
            id="glDailyTransItemBankSelect"
            class="gl-daily-trans-item-bank-select"
            label="Bank"
            item-text="bankName"
            item-value="_id"
            :rules="selectingBankValidation"
            :readonly="isReadOnly"
            @change=checkFund
            ref="isBankRef"
            @keydown.tab.prevent="handleTab(['isFromNoRef'])"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="editedItem.fromNumber"
            id="glDailyTransItemFromNoText"
            class="gl-daily-trans-item-fromNo-text"
            :readonly="isReadOnly"
            label="From No"
            ref="isFromNoRef"
            @keydown.tab="handleTab('isFromNoRef')"
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="editedItem.toNumber"
            id="glDailyTransItemToNoText"
            class="gl-daily-trans-item-toNo-text"
            label="To No"
            :readonly="isReadOnly"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3"
          v-if="editedItem.dailySection !== 'PAYMENTS'"
        >
          <v-text-field
            v-model="editedItem.deposit"
            id="glDailyTransItemDepositText"
            class="gl-daily-trans-item-deposit-text"
            label="Deposit"
            :readonly="isReadOnly"
            @blur="roundAmount"
          ></v-text-field>
        </v-col>
        <v-col cols="3"
          v-if="editedItem.dailySection !== 'PAYMENTS'"
        >
          <v-text-field
            v-model="editedItem.transferIn"
            id="glDailyTransItemTransferInText"
            class="gl-daily-trans-transfer-in-text"
            label="Transfer In"
            :readonly="isReadOnly"
            @blur="roundAmount"
          ></v-text-field>
        </v-col>
        <v-col cols="3"
          v-if="editedItem.dailySection !== 'PAYMENTS'"
        >
          <v-text-field
            v-model="editedItem.transferOut"
            id="glDailyTransItemTransferOutText"
            class="gl-daily-trans-transfer-out-text"
            label="Transfer Out"
            :readonly="isReadOnly"
            @blur="roundAmount"
          ></v-text-field>
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="editedItem.payments"
            id="glDailyTransItemPaymentText"
            class="gl-daily-trans-item-payment-text"
            label="Payment"
            :readonly="isReadOnly"
            @blur="roundAmount"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row class="d-flex align-end">
        <v-col cols="6">
          <v-textarea
            v-model="editedItem.description"
            id="glDailyTransItemDescriptionTextArea"
            class="gl-daily-trans-item-description-text-area"
            label="Description"
            rows="2"
            v-textAreaUppercase=globalSettingItem.conEverythingToCaps
            :readonly="isReadOnly"
          ></v-textarea>
        </v-col>
        <v-col cols="2"
         v-if="isTaggedRecord"
        >
          <v-checkbox id="glDailyTransItemVerifiedCheckbox"
            class="gl-daily-trans-item-verified-checkbox"
            v-model="editedItem.isVerified"
            label="Verified"
          ></v-checkbox>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import {
  mapGetters,
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';
import { amountFormat, amountToNumber } from '../../../util/shared/vue-global';
import ClarionDateControl from '../../common/ClarionDateControl.vue';
import GLTransactionCommon from './GLTransactionCommon.mixin';
import { buildStaleFundOption, glTaggedTransactionEnums } from './gl-transaction.util';
import GlobalToCaps from '../../../util/shared/GlobalToCaps.mixin.vue';

const fundCrudKey = 'sharedFund';

const amountFields = [
  'deposit',
  'transferIn',
  'transferOut',
  'payments',
];

export default {
  name: 'GlDailyEditedItem',
  mixins: [GLTransactionCommon, GlobalToCaps],
  components: {
    ClarionDateControl,
  },
  data: () => ({
    loadFundsList: [],
    fundsFromStore: [],
  }),
  props: {
    baseCrudKey: {
      type: String,
      default: 'glReceipt-dialog',
    },
  },
  created() {
  },
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'items',
      'index',
      'isVisible',
    ]),
    ...mapState({
      dailySectionTypes: (state) => state.enums.GlTransDailySectionEnum,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
    }),
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    isReadOnly() {
      return this.editedItem.isLocked;
    },
    isVisibleProp() {
      const isVisible = this.isVisible(this.baseCrudKey);
      if (isVisible) {
        this.$emit('activeItem', this.editedItem);
        if (this.criteriaOfFY) {
          this.loadFundsAPI();
        } else {
          this.getFundsFromStore();
        }
      }
      return [];
    },
    currentFundList() {
      return this.criteriaOfFY ? this.loadFundsList : this.fundsFromStore;
    },
    // the row's fund when it belongs to another fiscal year than the list below
    staleFundOption() {
      return buildStaleFundOption(this.editedItem.fund, this.currentFundList);
    },
    funds() {
      const updatedFundObj = this.currentFundList.map((f) => ({
        ...f,
        fundDesc: f.fund === '0000' ? f.description : `${f.fund} - ${f.description || ''}`,
      }));
      return [{ _id: null, fund: '', fundDesc: '' }, ...(this.staleFundOption ? [this.staleFundOption] : []), ...updatedFundObj];
    },
    staleFundWarning() {
      return this.staleFundOption ? 'This row uses a fund from another fiscal year - pick a fund from the current list.' : '';
    },
    criteriaOfFY() {
      return (this.criteria(this.baseCrudKey).fiscalYear === 0);
    },
    glDailySections() {
      if (!this.dailySectionTypes) {
        return [];
      }
      return this.dailySectionTypes;
    },
    showFunds() {
      return (this.editedItem.dailySection !== 'DEPOSITSANDCHECKS')
      || this.editedItem.dailySection === null;
    },
    showBanks() {
      return this.editedItem.dailySection !== 'COLLECTIONS'
      || this.editedItem.dailySection === null;
    },
    selectingFundValidation() {
      if (this.editedItem.bank !== null) {
        return [
          (value) => !value || 'Select either Bank/Fund!',
        ];
      }
      return [];
    },
    selectingBankValidation() {
      if (this.editedItem.fund !== null) {
        return [
          (value) => !value || 'Select either Bank/Fund!',
        ];
      }
      return [];
    },
    isTaggedRecord() {
      return glTaggedTransactionEnums().includes(this.editedItem.tag);
    },
    reqdSelectValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
  },
  methods: {
    amountFormat,
    ...mapMutations('base/crud', [
      'setItem',
    ]),
    ...mapActions('shared/fund', [
      'loadFunds',
    ]),
    handleTab(nextField) {
      let itemRef;
      if (this.showBanks && Array.isArray(nextField)) {
        itemRef = this.$refs[nextField[0]];
      } else if (Array.isArray(nextField)) {
        itemRef = this.$refs[nextField[1]];
      } else {
        itemRef = this.$refs[nextField];
      }
      if (itemRef) {
        itemRef.focus();
      }
    },
    async loadFundsAPI() {
      this.loadFundsList = await this.loadFunds({
        criteria: {
          fiscalYear: this.editedItem.fiscalYear,
          sortBy: [
            {
              column: 'fund',
              direction: 'asc',
            },
          ],
        },
        baseCrudKey: fundCrudKey,
      });
      return this.loadFundsList;
    },
    getFundsFromStore() {
      this.fundsFromStore = this.items(fundCrudKey) || [];
      return this.fundsFromStore;
    },
    checkBank() {
      if (this.editedItem.bank !== null) {
        this.editedItem.bank = null;
      }
      return this.editedItem.bank;
    },
    checkFund() {
      if (this.editedItem.fund !== null) {
        this.editedItem.fund = null;
      }
      return this.editedItem.fund;
    },
    roundAmount() {
      return amountFields.forEach((f) => {
        this.editedItem[f] = amountFormat(amountToNumber(this.editedItem[f]));
      });
    },
  },
};
</script>
